import React from 'react';
import App, { AppContext, AppProps } from 'next/app';
import Router, { useRouter } from 'next/router';
import { BrowserMetrics, WebVital } from '@spotify-internal/semantic-metrics';
import { sendMetric } from '../helpers/semanticMetrics';
import { UserProvider } from '../helpers/user/useUser';
import { I18nProvider } from '../helpers/i18n/useI18n';
import { Layout } from '../components/Layout';
import { PageAnalytics } from '@spotify-internal/s4mp-page-analytics';
import { withTRPC } from '@trpc/next';
import { httpBatchLink } from '@trpc/client/links/httpBatchLink';
import type { AppRouter } from '../server/routers/app';
import superjson from 'superjson';
import { getAuth } from '../helpers/user/auth';
import { useFeatureTest } from '../helpers/useFeatureTest';
import { UnsupportedBrowser } from '../components/UnsupportedBrowser';

const CustomApp = ({ Component, pageProps }: AppProps) => {
  const { locale } = useRouter();
  const isSupported = useFeatureTest();

  return (
    <>
      <I18nProvider locale={locale}>
        {isSupported ? (
          <UserProvider>
            <PageAnalytics />
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </UserProvider>
        ) : (
          <UnsupportedBrowser />
        )}
      </I18nProvider>
    </>
  );
};

export function reportWebVitals(metric: WebVital) {
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route: Router.router?.route ?? 'unknown',
    }),
  );
}

CustomApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  // trpc doesnt pass through app props, only page props. adding timestamp to pageprops to get around that
  return {
    ...appProps,
    pageProps: { ...appProps.pageProps, timestamp: Date.now().toString() },
  };
};

export default withTRPC<AppRouter>({
  config() {
    const url = '/api/trpc';
    return {
      url,
      transformer: superjson,
      links: [
        httpBatchLink({
          url,
          async headers() {
            const auth = getAuth();
            return {
              Authorization: `Bearer ${await auth!.getToken()}`,
            };
          },
        }),
      ],
    };
  },
  ssr: false,
})(CustomApp);
